export type ProntuarioSessionPrintField = {
	label: string;
	value: string;
	tags?: string;
};

export type ProntuarioSessionPrintPatient = {
	fullName: string;
	cpf?: string | null;
	rg?: string | null;
	birthDate?: string | null;
	gender?: string | null;
	maritalStatus?: string | null;
	educationLevel?: string | null;
	profession?: string | null;
	phone?: string | null;
	email?: string | null;
	emergencyContact?: string | null;
};

export type ProntuarioSessionPrintData = {
	patient: ProntuarioSessionPrintPatient;
	session: {
		number: number | null;
		dateLabel: string | null;
	};
	fields: ProntuarioSessionPrintField[];
};

const escapeHtml = (value: string) =>
	value
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#39;');

const formatMultiline = (value: string) =>
	escapeHtml(value).replaceAll('\n', '<br />');

const PRINT_TEXT_TAG_SEPARATOR = '—';

const formatSessionCardValueHtml = (field: ProntuarioSessionPrintField) => {
	const text = field.value?.trim() ?? '';
	const tags = field.tags?.trim() ?? '';

	if (!text && !tags) {
		return escapeHtml('—');
	}

	if (text && tags) {
		return `${formatMultiline(text)}<br />${escapeHtml(
			PRINT_TEXT_TAG_SEPARATOR,
		)}<br /><em class="session-card-tags">${escapeHtml(tags)}</em>`;
	}

	if (tags) {
		return `<em class="session-card-tags">${escapeHtml(tags)}</em>`;
	}

	return formatMultiline(text);
};

const formatRawNotesCardValueHtml = (field: ProntuarioSessionPrintField) => {
	const text = field.value?.trim() ?? '';
	const tags = field.tags?.trim() ?? '';
	const hasContent = Boolean(text || tags);

	const contentHtml = hasContent ? formatSessionCardValueHtml(field) : '';
	const linesHtml = `
		<div class="writing-lines${hasContent ? ' writing-lines--after-content' : ''}">
			${renderWritingLines(RAW_NOTES_WRITING_LINE_COUNT)}
		</div>
	`;

	return `${contentHtml}${linesHtml}`;
};

const renderSessionCard = (field: ProntuarioSessionPrintField) => {
	const isRawNotes = field.label === RAW_NOTES_FIELD_LABEL;
	const valueHtml = isRawNotes
		? formatRawNotesCardValueHtml(field)
		: formatSessionCardValueHtml(field);

	return `
		<article class="session-card${isRawNotes ? ' session-card--raw-notes' : ''}">
			<h4 class="session-card-label">${escapeHtml(field.label)}</h4>
			<div class="session-card-value">${valueHtml}</div>
		</article>
	`;
};

const formatPrintDate = () => {
	const now = new Date();
	return now.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
};

const WRITING_LINE_GAP = '2.2em';
const RAW_NOTES_FIELD_LABEL = 'Anotações livres';
const RAW_NOTES_WRITING_LINE_COUNT = 5;

const renderWritingLines = (lineCount: number) =>
	Array.from(
		{ length: lineCount },
		() => '<div class="writing-line"></div>',
	).join('');

const renderDataCell = (
	label: string,
	value: string | null | undefined,
	className = '',
) => `
	<div class="data-cell ${className}">
		<span class="data-label">${escapeHtml(label)}</span>
		<span class="data-value">${escapeHtml(value?.trim() || '—')}</span>
	</div>
`;

const buildPatientIdentificationHtml = (
	patient: ProntuarioSessionPrintPatient,
) => `
	<section class="doc-section patient-section">
		<div class="section-banner">
			<span class="section-title">Identificação do Paciente</span>
			<span class="section-banner-note">Dados cadastrais</span>
		</div>
		<div class="data-grid">
			<div class="data-row">
				${renderDataCell('Nome completo:', patient.fullName, 'data-cell--wide')}
				${renderDataCell('CPF:', patient.cpf)}
				${renderDataCell('RG:', patient.rg)}
			</div>
			<div class="data-row">
				${renderDataCell('Data de nascimento:', patient.birthDate)}
				${renderDataCell('Sexo:', patient.gender)}
				${renderDataCell('Estado civil:', patient.maritalStatus)}
				${renderDataCell('Escolaridade:', patient.educationLevel)}
			</div>
			<div class="data-row">
				${renderDataCell('Profissão:', patient.profession)}
				${renderDataCell('Telefone:', patient.phone)}
				${renderDataCell('E-mail:', patient.email)}
			</div>
			<div class="data-row">
				${renderDataCell(
					'Contato de emergência:',
					patient.emergencyContact,
					'data-cell--full',
				)}
			</div>
		</div>
	</section>
`;

const sanitizePrintFilename = (value: string) =>
	value
		.replace(/[\\/:*?"<>|]/g, '')
		.replace(/\s+/g, ' ')
		.trim();

export const buildProntuarioPrintDocumentTitle = (
	data: ProntuarioSessionPrintData,
): string => {
	const patientName = data.patient.fullName.trim() || 'Paciente';
	const sessionSuffix =
		data.session.number != null
			? `sessao ${data.session.number}`
			: 'sessao';

	return sanitizePrintFilename(`${patientName} - ${sessionSuffix} - Plumaa`);
};

const buildPrintHtml = (
	data: ProntuarioSessionPrintData,
	documentTitle: string,
) => {
	const sessionNumberLabel =
		data.session.number != null
			? `Sessão ${data.session.number}`
			: 'Sessão —';
	const printDate = formatPrintDate();

	const attendanceField = data.fields.find(
		(field) => field.label === 'Presença',
	);
	const sessionFields = data.fields.filter(
		(field) => field.label !== 'Presença',
	);

	const fieldsHtml = sessionFields
		.map((field) => renderSessionCard(field))
		.join('');

	return `<!DOCTYPE html>
<html lang="pt-BR">
	<head>
		<meta charset="utf-8" />
		<title>${escapeHtml(documentTitle)}</title>
		<style>
			@page {
				size: A4;
				margin: 14mm 16mm;
			}

			* {
				box-sizing: border-box;
			}

			body {
				margin: 0;
				font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
				font-size: 10.5pt;
				line-height: 1.5;
				color: #1e293b;
				background: #fff;
				-webkit-print-color-adjust: exact;
				print-color-adjust: exact;
			}

			.print-root {
				width: 100%;
				max-width: 178mm;
				margin: 0 auto;
			}

			.doc-header {
				margin-bottom: 20px;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.doc-header-top {
				display: flex;
				justify-content: space-between;
				align-items: flex-start;
				gap: 20px;
			}

			.doc-title {
				margin: 0;
				font-size: 17pt;
				font-weight: 800;
				letter-spacing: 0.02em;
				text-transform: uppercase;
				color: #0f172a;
				line-height: 1.15;
			}

			.doc-meta {
				flex-shrink: 0;
				text-align: right;
				font-family: ui-monospace, 'Cascadia Code', 'Segoe UI Mono', monospace;
				font-size: 8.5pt;
				line-height: 1.6;
				color: #334155;
			}

			.doc-header-rule {
				margin: 14px 0 0;
				border: 0;
				border-top: 3px solid #0f172a;
			}

			.doc-section {
				margin-bottom: 24px;
			}

			.section-banner {
				display: flex;
				justify-content: space-between;
				align-items: center;
				gap: 12px;
				padding: 8px 12px;
				margin-bottom: 0;
				background: #f1f5f9;
				border: 1px solid #e2e8f0;
				border-bottom: none;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.section-title {
				margin: 0 0 6px;
				font-size: 9pt;
				font-weight: 800;
				letter-spacing: 0.06em;
				text-transform: uppercase;
				color: #0f172a;
			}

			.section-banner .section-title {
				margin: 0;
			}

			.section-banner-note {
				font-size: 8pt;
				color: #64748b;
			}

			.data-grid {
				border: 1px solid #e2e8f0;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.data-row {
				display: flex;
				border-bottom: 1px solid #e2e8f0;
			}

			.data-row:last-child {
				border-bottom: none;
			}

			.data-cell {
				flex: 1;
				min-width: 0;
				padding: 10px 12px;
				border-right: 1px solid #e2e8f0;
			}

			.data-cell:last-child {
				border-right: none;
			}

			.data-cell--wide {
				flex: 2;
			}

			.data-cell--full {
				flex: 1 1 100%;
			}

			.data-label {
				display: block;
				margin-bottom: 3px;
				font-size: 7pt;
				font-weight: 700;
				letter-spacing: 0.08em;
				text-transform: uppercase;
				color: #64748b;
			}

			.data-value {
				display: block;
				font-size: 10pt;
				font-weight: 500;
				color: #0f172a;
				word-break: break-word;
			}

			.manual-section {
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.manual-hint {
				margin: 0 0 ${WRITING_LINE_GAP};
				font-size: 8.5pt;
				font-style: italic;
				color: #64748b;
			}

			.writing-lines {
				display: flex;
				flex-direction: column;
				gap: ${WRITING_LINE_GAP};
			}

			.writing-line {
				border-bottom: 1px solid #cbd5e1;
			}

			.writing-lines--after-content {
				margin-top: ${WRITING_LINE_GAP};
			}

			.session-card--raw-notes .session-card-value {
				white-space: normal;
			}

			.session-section {
				margin-top: 28px;
			}

			.session-header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				gap: 16px;
				margin-bottom: 16px;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.session-title-wrap {
				display: flex;
				align-items: center;
				min-width: 0;
			}

			.session-number {
				color: #1d4ed8;
			}

			.session-meta-badges {
				display: flex;
				align-items: center;
				gap: 8px;
				flex-shrink: 0;
			}

			.session-meta-badge {
				flex-shrink: 0;
				padding: 6px 12px;
				border-radius: 6px;
				background: #f1f5f9;
				border: 1px solid #e2e8f0;
				font-size: 9pt;
				font-weight: 600;
				color: #334155;
				white-space: nowrap;
			}

			.session-cards {
				display: block;
			}

			.session-card {
				margin-bottom: 12px;
				padding: 14px 16px;
				border: 1px solid #e2e8f0;
				border-radius: 8px;
				background: #fff;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.session-card:last-child {
				margin-bottom: 0;
			}

			.session-card-label {
				margin: 0 0 8px;
				font-size: 8pt;
				font-weight: 800;
				letter-spacing: 0.07em;
				text-transform: uppercase;
				color: #475569;
			}

			.session-card-value {
				font-size: 10pt;
				line-height: 1.55;
				color: #1e293b;
				white-space: pre-wrap;
				word-break: break-word;
			}

			.session-card-tags {
				display: block;
				margin-top: 0.15em;
				font-style: italic;
				color: #475569;
			}
		</style>
	</head>
	<body>
		<div class="print-root">
			<header class="doc-header">
				<div class="doc-header-top">
					<div>
						<h1 class="doc-title">Prontuário de Atendimento Clínico</h1>
					</div>
					<div class="doc-meta">
						<div>Data de Impressão: ${escapeHtml(printDate)}</div>
					</div>
				</div>
				<hr class="doc-header-rule" />
			</header>

			${buildPatientIdentificationHtml(data.patient)}

			<section class="doc-section manual-section">
				<h2 class="section-title">Informações Adicionais</h2>
				<p class="manual-hint">Anotações adicionais</p>
				<div class="writing-lines">
					${renderWritingLines(3)}
				</div>
			</section>

			<section class="doc-section session-section">
				<div class="session-header">
					<div class="session-title-wrap">
						<h2 class="section-title">
							Registro da Sessão:
							<span class="session-number">${escapeHtml(sessionNumberLabel)}</span>
						</h2>
					</div>
					${
						attendanceField || data.session.dateLabel
							? `<div class="session-meta-badges">
									${
										attendanceField
											? `<div class="session-meta-badge">${escapeHtml(
													attendanceField.value,
											  )}</div>`
											: ''
									}
									${
										data.session.dateLabel
											? `<div class="session-meta-badge">Data: ${escapeHtml(
													data.session.dateLabel,
											  )}</div>`
											: ''
									}
								</div>`
							: ''
					}
				</div>

				<div class="session-cards">
					${fieldsHtml}
				</div>
			</section>
		</div>
	</body>
</html>`;
};

export const printProntuarioSession = (
	data: ProntuarioSessionPrintData,
): boolean => {
	const documentTitle = buildProntuarioPrintDocumentTitle(data);
	const html = buildPrintHtml(data, documentTitle);
	const originalTitle = document.title;

	document.title = documentTitle;

	const iframe = document.createElement('iframe');

	iframe.setAttribute('title', documentTitle);
	iframe.style.cssText =
		'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';

	document.body.appendChild(iframe);

	const printWindow = iframe.contentWindow;
	const iframeDocument =
		iframe.contentDocument ?? printWindow?.document ?? null;

	if (!printWindow || !iframeDocument) {
		document.title = originalTitle;
		iframe.remove();
		return false;
	}

	iframeDocument.open();
	iframeDocument.write(html);
	iframeDocument.close();
	iframeDocument.title = documentTitle;

	let cleanedUp = false;

	const cleanup = () => {
		if (cleanedUp) return;
		cleanedUp = true;
		document.title = originalTitle;
		iframe.remove();
	};

	let cleanupTimeout = window.setTimeout(cleanup, 60_000);

	const handleAfterPrint = () => {
		window.clearTimeout(cleanupTimeout);
		cleanup();
	};

	const triggerPrint = () => {
		printWindow.focus();
		printWindow.print();
	};

	window.addEventListener('afterprint', handleAfterPrint, { once: true });
	printWindow.addEventListener('afterprint', handleAfterPrint, {
		once: true,
	});

	if (iframeDocument.readyState === 'complete') {
		triggerPrint();
	} else {
		iframe.addEventListener('load', triggerPrint, { once: true });
	}

	return true;
};
